import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useLocale } from "next-intl";
import {
  sendPublicLearningEvent,
  type PublicLearningAction,
} from "@/lib/api/public-learning";
import {
  learningLocale,
  unlocalizedLearningPath,
} from "@/lib/public-learning-routes";
import {
  pendingLearningEvents,
  readLearningVisit,
  touchLearningVisit,
} from "@/lib/public-learning-visit";
import pages from "../../../config/content/public_learning.json";

type Track = (event: PublicLearningAction, strategyId?: string) => void;
const Context = createContext<Track>(() => {});
export const usePublicLearningTelemetry = () => useContext(Context);

export function PublicLearningTelemetryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const locale = useLocale();
  const sent = useRef(new Set<string>());
  const inFlight = useRef(new Set<string>());
  const path = pathname.replace(/\/$/, "") || "/";
  const isPublic =
    path === "/" ||
    path === "/manifold" ||
    pages.some((p) => p.path === unlocalizedLearningPath(path));
  const track = useCallback<Track>(
    (event, strategyId) => {
      const visit = isPublic
        ? touchLearningVisit(path, learningLocale(path) ?? locale)
        : readLearningVisit();
      if (!visit) return;
      const key = `${visit.id}:${event}:${visit.path}`;
      if (sent.current.has(key) || inFlight.current.has(key)) return;
      inFlight.current.add(key);
      const promise = sendPublicLearningEvent({
        event,
        visit_id: visit.id,
        path: visit.path,
        locale: visit.locale,
        source: visit.source,
        strategy_id: strategyId,
      })
        .then(() => {
          sent.current.add(key);
        })
        .catch(() => {
          /* Analytics must not interrupt learning or saving. */
        })
        .finally(() => {
          inFlight.current.delete(key);
          pendingLearningEvents.delete(promise);
        });
      pendingLearningEvents.add(promise);
    },
    [isPublic, path, locale],
  );
  useEffect(() => {
    if (!isPublic) return;
    track("page_view");
    if (path.endsWith("/explore")) track("simulator_opened");
  }, [isPublic, path, track]);
  return <Context.Provider value={track}>{children}</Context.Provider>;
}
